import React, { useState } from 'react';
import { Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { SECTORS } from './LeadFilters';
import { StatusBadge } from './StatusBadge';

const EMPTY_FORM = {
  business_name: '', sector: 'unknown', city: '', province: '',
  website_url: '', phone: '', email: '', instagram_url: '', notes: '',
};

// matched_by ("domain"|"phone"|"name_city") viene del backend (Commit 14
// 3/3, pieza aislada previa) — el backend solo informa el criterio real,
// el frontend decide cómo redactarlo de forma legible para Marta.
function duplicateReasonKey(matchedBy) {
  if (matchedBy === 'domain') return 'aiLeadScanner.addLeadModal.duplicateReasonDomain';
  if (matchedBy === 'phone') return 'aiLeadScanner.addLeadModal.duplicateReasonPhone';
  return 'aiLeadScanner.addLeadModal.duplicateReasonNameCity';
}

export function AddLeadModal({ open, onOpenChange, onCreated }) {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // { existing_lead, matched_by } | null

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setDuplicate(null);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!form.business_name.trim()) {
      toast.error(t('aiLeadScanner.addLeadModal.businessNameRequired'));
      return;
    }

    setSubmitting(true);
    setDuplicate(null);
    try {
      const payload = {};
      for (const [key, value] of Object.entries(form)) {
        payload[key] = value.trim() ? value.trim() : undefined;
      }
      const res = await api.post('/leads', payload);

      if (res.data.created) {
        toast.success(t('aiLeadScanner.addLeadModal.success'));
        onCreated?.();
        resetAndClose();
      } else {
        // Duplicado real detectado por el backend — no es un error genérico,
        // se muestra de forma persistente en el propio modal (no un toast
        // que desaparece) para que Marta pueda decidir con calma.
        setDuplicate({ existingLead: res.data.existing_lead, matchedBy: res.data.matched_by });
      }
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error(t('aiLeadScanner.addLeadModal.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewExisting = () => {
    resetAndClose();
    navigate(`/internal/ai-lead-scanner/leads/${duplicate.existingLead.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) resetAndClose(); else onOpenChange(next); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('aiLeadScanner.actions.addLead')}</DialogTitle>
        </DialogHeader>

        {duplicate && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-amber-400">
                  {t(duplicateReasonKey(duplicate.matchedBy), { name: duplicate.existingLead.business_name })}
                </p>
                <StatusBadge status={duplicate.existingLead.status} />
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={handleViewExisting}>
                {t('aiLeadScanner.addLeadModal.viewExisting')}
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>{t('aiLeadScanner.addLeadModal.businessName')} *</Label>
            <Input value={form.business_name} onChange={handleChange('business_name')} className="mt-1" data-testid="add-lead-business-name" />
          </div>

          <div>
            <Label>{t('aiLeadScanner.filters.sector')}</Label>
            <Select value={form.sector} onValueChange={(v) => setForm((p) => ({ ...p, sector: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SECTORS.map((s) => (
                  <SelectItem key={s} value={s}>{t(`aiLeadScanner.sector.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('aiLeadScanner.filters.city')}</Label>
              <Input value={form.city} onChange={handleChange('city')} className="mt-1" />
            </div>
            <div>
              <Label>{t('aiLeadScanner.addLeadModal.province')}</Label>
              <Input value={form.province} onChange={handleChange('province')} className="mt-1" />
            </div>
          </div>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.website')}</Label>
            <Input value={form.website_url} onChange={handleChange('website_url')} className="mt-1" placeholder="https://" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('aiLeadScanner.addLeadModal.phone')}</Label>
              <Input value={form.phone} onChange={handleChange('phone')} className="mt-1" />
            </div>
            <div>
              <Label>{t('aiLeadScanner.addLeadModal.email')}</Label>
              <Input value={form.email} onChange={handleChange('email')} className="mt-1" type="email" />
            </div>
          </div>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.instagram')}</Label>
            <Input value={form.instagram_url} onChange={handleChange('instagram_url')} className="mt-1" placeholder="https://instagram.com/..." />
          </div>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.notes')}</Label>
            <Textarea value={form.notes} onChange={handleChange('notes')} className="mt-1" rows={3} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={resetAndClose} disabled={submitting}>
              {t('aiLeadScanner.leadDetail.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="add-lead-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {t('aiLeadScanner.addLeadModal.create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
